import { Libraries, useLoadScript } from "@react-google-maps/api";
import usePlacesAutocomplete, {
  Suggestion,
  getDetails,
} from "use-places-autocomplete";
import { v4 as uuid } from "uuid";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  AddressType,
  CountryInfo,
  OrganisationAddress,
  UserAddress,
} from "@/api/SolarCloudApi";
import { CountryOptions, useCountries } from "@/hooks/Countries";
import { error } from "@/components/Messages";
import { useEnvironmentContext } from "@/contexts/EnvironmentContext";
import { Input } from "@/components/ui/input";
import { extractCountryCodes } from "@/util/extractCountryCodes";

type AddressUnion = UserAddress | OrganisationAddress;

type Props = {
  placeholder?: string;
  address?: AddressUnion;
  setAddress: (address: AddressUnion) => void;
  options?: CountryOptions;
  className?: string;
  countries?: CountryInfo[];
  value?: string;
  onChange?: (value: string) => void;
};

const extractAddressComponent = (
  details: google.maps.places.PlaceResult,
  name: string,
  short?: boolean
) => {
  return (
    details.address_components?.find((comp) => comp.types.includes(name))?.[
      short ? "short_name" : "long_name"
    ] ?? undefined
  );
};

const libraries: Libraries = ["places"];

export function LocationAutocomplete({
  placeholder,
  address,
  setAddress,
  options,
  className,
  countries,
  value: externalValue,
  onChange,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const hasInit = useRef(false);

  const environment = useEnvironmentContext();
  const { countries: hookCountries, isLoading } = useCountries(options);
  const finalCountries = countries || hookCountries;
  const countryCodes = extractCountryCodes(finalCountries);

  const { isLoaded: isScriptLoaded, loadError: scriptLoadError } =
    useLoadScript({
      googleMapsApiKey: environment?.google.apiKey || "",
      libraries,
    });

  const { init, value, setValue, suggestions, clearSuggestions } =
    usePlacesAutocomplete({
      requestOptions: {
        componentRestrictions: {
          country: countryCodes ?? null,
        },
      },
      initOnMount: false,
      debounce: 100,
    });

  useEffect(() => {
    if (isScriptLoaded && !isLoading && !hasInit.current) {
      init();
      hasInit.current = true;
      if (externalValue) {
        setValue(externalValue, false);
      }
    }
  }, [isScriptLoaded, isLoading, init, externalValue, setValue]);

  useEffect(() => {
    if (scriptLoadError) {
      console.error("Script load error:", scriptLoadError);
      error(
        "Address lookup script failed to load. Please enter address manually."
      );
    }
  }, [scriptLoadError]);

  useEffect(() => {
    if (externalValue !== undefined && externalValue !== value) {
      setValue(externalValue, false);
    }
  }, [externalValue, value, setValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    onChange?.(newValue);
    if (!isOpen) setIsOpen(true);
  };

  const handleSelection = async (suggestion: Suggestion) => {
    try {
      setValue("", false);
      clearSuggestions();
      setIsOpen(false);

      if (suggestion.place_id) {
        const details = (await getDetails({
          placeId: suggestion.place_id,
          fields: ["address_components"],
        })) as google.maps.places.PlaceResult;

        const newAddress: UserAddress = {
          uuid: address?.uuid ?? uuid(),
          type: AddressType.POSTAL,
          line1:
            suggestion.structured_formatting?.main_text ||
            suggestion.description,
          city: extractAddressComponent(details, "locality"),
          state: extractAddressComponent(
            details,
            "administrative_area_level_1",
            true
          ),
          postcode: extractAddressComponent(details, "postal_code"),
          country: extractAddressComponent(details, "country"),
        };

        setAddress?.(newAddress);
      }
    } catch (err) {
      console.error("Error getting place details:", err);
    }
  };

  const displayValue = externalValue ?? value;

  const suggestionsList = useMemo(() => {
    return suggestions.data.length > 0 ? (
      <ul className="text-sm p-1">
        {suggestions.data.map((suggestion, idx) => (
          <li
            key={suggestion.place_id || idx}
            className="cursor-pointer px-3 py-2 hover:bg-brand-neutral-800 text-white transition-colors duration-150 rounded-sm"
            onMouseDown={(e) => {
              e.preventDefault();
              handleSelection(suggestion);
            }}
          >
            {suggestion.description}
          </li>
        ))}
      </ul>
    ) : (
      <div className="px-3 py-6 text-sm text-brand-neutral-400 text-center">
        Enter your address
      </div>
    );
  }, [suggestions.data, handleSelection]);

  return (
    <div className={`relative w-full ${className || ""}`}>
      <Input
        placeholder={placeholder ?? "Enter address"}
        value={displayValue}
        onFocus={() => {
          if (displayValue?.trim()) {
            setValue(displayValue, true);
          }
          if (!isOpen) setIsOpen(true);
        }}
        onBlur={() => {
          setTimeout(() => setIsOpen(false), 100);
        }}
        onChange={handleInputChange}
        disabled={!isScriptLoaded || isLoading}
        className="h-10 bg-brand-neutral-800 border-brand-neutral-700 text-white placeholder:text-brand-neutral-400 focus:border-brand-primary-500 focus:ring-brand-primary-500"
      />
      {isOpen && suggestions.data.length > 0 && value.length > 0 && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-popover border border-brand-neutral-700 rounded-md shadow-lg max-h-64 overflow-y-auto animate-in fade-in-0 slide-in-from-top-2 slide-in-from-left-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=closed]:slide-out-to-left-2">
          {suggestionsList}
        </div>
      )}
    </div>
  );
}
